import React, { useEffect } from "react";
import { useAppSelector } from "../../app/hooks";
import { IDialog, IOption } from "../../app/types";
import { ScreenRenderer } from "..";
import { setTopbarData } from "../AppBar/AppBarUtils";
import { GridRenderCellParams } from "@mui/x-data-grid";
import {
  populateButtonClickRequestParams,
  populateOptionClickRequestParams,
  populateLinkClickRequestParams,
  establishSessionAndSendQueryParamsPageReq,
  establishSessionAndSendInitialPageReq,
  sendInitialPageRequest,
  populateBackClickRequestParams,
  populateBreadCrumbClickRequestParams,
  sendKeepAliveRequest,
  reloadApplication,
  sendLogoffRequest,
  backToLogin,
  // resendRequestAfterTimeout,
  populateQueryParamsRequest,
} from "../client/clientUtils";
import { setMainContentDialogPopUp } from "../MainContent/MainContentSlice";
import { setAppBarLoginInfo } from "../AppBar/AppBarSlice";
import { getCookie } from "../../app/utils/cookies";
import { IField } from "../../app/types";
import { store } from "../../app/store";

/* Functional component properties */
interface Props {
  classes: any;
  startupData: any;
}

/* React Functional component */
export const PageController: React.FC<Props> = ({
  /* Function arguments (Props) */
  classes,
  startupData,
}): JSX.Element => {
  window.myGlobalVar = window.myGlobalVar || {};

  /* get state */
  const state = useAppSelector((state) => state);

  /* Timer running in every 30 seconds for keep alive server request */
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionStorage.getItem("keepAliveTimer")) {
        /* Difference between time is more than 30 minutes then send keepAlive request to the server */
        if (
          Date.now() - Number(sessionStorage.getItem("keepAliveTimer")) >=
          30 * 60 * 1000
        ) {
          sendKeepAliveRequest();
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  function keyDownEventHandler(event: KeyboardEvent) {
    if (
      event.key === "Enter" &&
      event.target &&
      (event.target as HTMLElement).id &&
      (event.target as HTMLElement).id != ""
    ) {
      if (
        (event.target as HTMLElement).id.indexOf("F") === 0 &&
        document.getElementById("SEARCH") != undefined
      )
        buttonClickHandler({ name: "SEARCH" } as IField);
      else if (document.getElementById("GO") != undefined)
        buttonClickHandler({ name: "GO" } as IField);
    }
  }

  function popStateEventHandler() {
    buttonClickHandler({ name: "BACK" } as IField);
  }

  function synchroniseSessionStorage() {
    if (
      localStorage.getItem("persist:rootLocal") != null &&
      getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) != "" &&
      (Object.keys(state.appBar.loginInfo).length === 0 ||
        (Object.keys(state.appBar.loginInfo).length > 0 &&
          state.appBar.loginInfo.data &&
          state.appBar.loginInfo.data.jobnumber !=
            JSON.parse(
              JSON.parse(localStorage.getItem("persist:rootLocal")!).loginInfo
            ).data.jobnumber))
    ) {
      store.dispatch(
        setAppBarLoginInfo(
          JSON.parse(
            JSON.parse(localStorage.getItem("persist:rootLocal")!).loginInfo
          )
        )
      );
    }
  }

  /* Invoke startup action to read initial page response from server */
  useEffect(() => {
    window.myGlobalVar.pendingChanges = {
      status: false,
      items: [],
      showWarning: false,
    };

    /* Add event listener for back button */
    document.removeEventListener("keydown", keyDownEventHandler);
    document.addEventListener("keydown", keyDownEventHandler);

    window.removeEventListener("popstate", popStateEventHandler);
    window.addEventListener("popstate", popStateEventHandler);

    /* fetch build information and set it in the appbar */
    setTopbarData();

    synchroniseSessionStorage();

    if (
      /* If session cookie is not there and page is not LOGOFF then establish new session */
      (getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) === "" &&
        state.mainContent.screen !== "LOGOFF") ||
      /* If session cookie is not there and persisted session storage is missing */
      (getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) === "" &&
        (localStorage.getItem("persist:rootLocal") === null ||
          (localStorage.getItem("persist:rootLocal") != null &&
            Object.keys(
              JSON.parse(localStorage.getItem("persist:rootLocal")!).loginInfo
            ).length === 0)))
    ) {
      console.log("Sending establishSessionAndSendInitialPageReq");
      if (
        document.location.search != "" &&
        document.location.search.indexOf("?") === 0 &&
        document.location.search.search(/function/gi) > 0 &&
        document.location.search.search(/key/gi) > 0
      )
        establishSessionAndSendQueryParamsPageReq();
      else establishSessionAndSendInitialPageReq();
    } else if (
      (localStorage.getItem("persist:rootLocal") === null ||
        state.mainContent.screen === "") &&
      getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) !== ""
    ) {
      console.log(
        "Session Cookie is already there so just sending initial page req"
      );

      if (
        document.location.search != "" &&
        document.location.search.indexOf("?") === 0 &&
        document.location.search.search(/function/gi) > 0 &&
        document.location.search.search(/key/gi) > 0
      ) {
        console.log("Sending params request");
        populateQueryParamsRequest();
      } else sendInitialPageRequest();
    } else if (
      getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) !== "" &&
      document.location.search != "" &&
      document.location.search.indexOf("?") === 0 &&
      document.location.search.search(/function/gi) > 0 &&
      document.location.search.search(/key/gi) > 0
    ) {
      console.log("Sending params request");
      populateQueryParamsRequest();
    }
  }, []);

  /* Button click event handler to simulate page navigation */
  async function buttonClickHandler(button: IField, caption?: string) {
    console.log("buttonClickHandler : ", button);
    synchroniseSessionStorage();

    if (
      getCookie(
        `Session_${startupData.sessionPayload.application}_${startupData.sessionPayload.environment}`
      ) !== "" ||
      button.name === "BACKTOLOGIN"
    ) {
      if (
        window.myGlobalVar.pendingChanges &&
        window.myGlobalVar.pendingChanges.status &&
        button.name === "BACK"
      ) {
        // Saving back click params to window variable for later use
        window.myGlobalVar.saveBackClick = {
          button: button,
          caption: caption || "",
        };

        window.myGlobalVar.pendingChanges.showWarning = true;

        store.dispatch(
          setMainContentDialogPopUp({
            header: "Alert",
            content: {
              title:
                "You have pending changes. Are you sure you want to leave the page ?",
            },
            action: ["CANCEL", "CONTINUE"],
          })
        );
      } else if (
        window.myGlobalVar.pendingChanges &&
        window.myGlobalVar.pendingChanges.status &&
        button.name === "CANCEL"
      ) {
        store.dispatch(setMainContentDialogPopUp({} as IDialog));
      } else if (
        window.myGlobalVar.pendingChanges &&
        window.myGlobalVar.pendingChanges.status &&
        button.name === "CONTINUE"
      ) {
        window.myGlobalVar.pendingChanges = {
          status: false,
          items: [],
          showWarning: false,
        };

        if (window.myGlobalVar.saveBackClick.caption != "")
          populateBreadCrumbClickRequestParams(
            window.myGlobalVar.saveBackClick.caption
          );
        else
          populateBackClickRequestParams(
            window.myGlobalVar.saveBackClick.button.name
          );
      } else if (button.name === "BACK") {
        if (caption) populateBreadCrumbClickRequestParams(caption || "");
        else populateBackClickRequestParams(button.name);
      } else if (button.name === "LOGOFF") {
        sendLogoffRequest();
      } else if (button.name === "RELOAD") {
        reloadApplication();
      } else if (button.name === "BACKTOLOGIN") {
        backToLogin();
      } else {
        if (
          document.location.search != "" &&
          document.location.search.indexOf("?") === 0 &&
          document.location.search.search(/function/gi) > 0 &&
          document.location.search.search(/key/gi) > 0
        )
          populateQueryParamsRequest();
        else populateButtonClickRequestParams(button);
      }
    } else {
      window.location.reload();
    }
  }

  /* Link click event handler to simulate page navigation */
  async function optionClickHandler(option: IOption) {
    console.log("optionClickHandler: ", option);
    synchroniseSessionStorage();
    populateOptionClickRequestParams(option);
  }

  /* Link click event handler to simulate page navigation */
  async function linkClickHandler(link: GridRenderCellParams) {
    console.log("linkClickHandler: ", link);
    synchroniseSessionStorage();
    populateLinkClickRequestParams(link);
  }

  return (
    <ScreenRenderer
      classes={classes}
      key={"screen"}
      buttonClickHandler={buttonClickHandler}
      optionClickHandler={optionClickHandler}
      linkClickHandler={linkClickHandler}
    />
  );
};
